import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Autocomplete from "@mui/material/Autocomplete"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"

import { parseApiErrorMessage } from "../../../common/apiErrors"
import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import { isConcierge } from "../../users/accountRole"
import { selectProfile } from "../../users/reducer"
import DialogHeader from "../components/DialogHeader"
import {
  TemplateGroupTabs,
  useTemplatePickerTab,
} from "../components/templateGroups"

const DIALOG_Z_INDEX = 1900
const MENU_Z_INDEX = 2000

function normalizeTemplateList(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  return []
}

export default function MissedYouEmailButton({ candidate, visible }) {
  const profile = useSelector(selectProfile)
  const isConciergeUser = isConcierge(profile)
  const isVisible = Boolean(visible && isConciergeUser)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [sent, setSent] = useState(false)

  useEffect(() => {
    if (!isVisible) {
      setDialogOpen(false)
      setSent(false)
    }
  }, [isVisible])

  if (!isVisible) return null

  const hasEmail = Boolean(candidate?.email)

  return (
    <Column gap={0.75}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<EmailOutlinedIcon />}
        disabled={sent || !hasEmail}
        onClick={() => setDialogOpen(true)}
        sx={{
          textTransform: "none",
          fontWeight: 700,
          alignSelf: "flex-start",
        }}
      >
        Send missed-you email
      </Button>
      {!hasEmail ? (
        <Typography variant="body2" color="text.secondary">
          This lead has no email address.
        </Typography>
      ) : null}
      {sent ? (
        <Typography variant="body2" color="success.main">
          Missed-you email sent.
        </Typography>
      ) : null}
      <MissedYouEmailDialog
        open={dialogOpen}
        candidate={candidate}
        onClose={() => setDialogOpen(false)}
        onSent={() => {
          setSent(true)
          setDialogOpen(false)
        }}
      />
    </Column>
  )
}

function MissedYouEmailDialog({ open, candidate, onClose, onSent }) {
  const axios = useAuthorizedAxiosClient()
  const [templates, setTemplates] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [selected, setSelected] = useState(null)
  const [inputValue, setInputValue] = useState("")
  const [showPreview, setShowPreview] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState("")
  const { tab, setTab, options } = useTemplatePickerTab(templates)

  useEffect(() => {
    if (!open) {
      setSelected(null)
      setInputValue("")
      setShowPreview(false)
      setError("")
      return undefined
    }

    let cancelled = false
    setIsLoading(true)
    const campaignsUrl =
      candidate?.id && candidate?.isLead === false
        ? `/workflows/api/email/select-campaign/${candidate.id}/`
        : "/workflows/api/email/select-campaign/"

    axios
      .get(campaignsUrl)
      .then(({ data }) => {
        if (!cancelled) {
          const list = normalizeTemplateList(data).map((item) => ({
            ...item,
            label: item.name || item.subject || `Template ${item.id}`,
          }))
          setTemplates(list)
        }
      })
      .catch(() => {
        if (!cancelled) setTemplates([])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [axios, candidate, open])

  async function handleSendClicked() {
    if (!selected?.id || !candidate?.email) return
    setIsSending(true)
    setError("")
    try {
      const emailBody = selected.rendered_body || selected.html_body || ""
      if (candidate?.id && candidate?.isLead === false) {
        await axios.post(
          `/workflows/api/email/send-campaign/${selected.id}/candidate/${candidate.id}/`,
          { email_body: emailBody }
        )
      } else {
        await axios.post(
          `/workflows/api/email/send-campaign/${selected.id}/lead/`,
          {
            name: candidate.candidate_name || candidate.name || "",
            phone: candidate.phone || "",
            email: candidate.email,
            company_name: candidate.company_name || "",
            email_body: emailBody,
          }
        )
      }
      onSent()
    } catch (err) {
      setError(
        parseApiErrorMessage(
          err?.response?.data,
          "Could not send the missed-you email."
        )
      )
    } finally {
      setIsSending(false)
    }
  }

  const previewHtml =
    selected?.rendered_body || selected?.html_body || selected?.content || ""

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: DIALOG_Z_INDEX }}
    >
      <DialogHeader title="Send missed-you email" onClose={onClose} />
      <DialogContent>
        <Column gap={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Choose an email template, preview it, then send it to{" "}
            {candidate?.candidate_name || candidate?.name || "this lead"}.
          </Typography>
          <TemplateGroupTabs tab={tab} onChange={setTab} />
          <Autocomplete
            value={selected}
            onChange={(_, value) => {
              setSelected(value)
              setShowPreview(false)
              setError("")
            }}
            inputValue={inputValue}
            onInputChange={(_, value) => setInputValue(value)}
            options={options}
            loading={isLoading}
            getOptionLabel={(option) => option.label || option.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Email template"
                placeholder="Select an email template"
              />
            )}
            componentsProps={{
              popper: { sx: { zIndex: MENU_Z_INDEX } },
            }}
            noOptionsText={
              isLoading ? "Loading templates…" : "No email templates found"
            }
          />
          {templates.length === 0 && !isLoading ? (
            <Typography variant="body2" color="text.secondary">
              Add an email template in Email Campaigns to send a missed-you
              email from here.
            </Typography>
          ) : null}
          {showPreview ? (
            <Column gap={1}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Subject: {selected?.subject || "(No subject)"}
              </Typography>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: "colors.gray50",
                  border: "1px solid",
                  borderColor: "colors.gray200",
                  maxHeight: 250,
                  overflowY: "auto",
                }}
                dangerouslySetInnerHTML={{
                  __html:
                    previewHtml ||
                    "<em>This template has no body content.</em>",
                }}
              />
            </Column>
          ) : null}
          {error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : null}
        </Column>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<VisibilityOutlinedIcon />}
          disabled={!selected}
          onClick={() => setShowPreview(true)}
        >
          Preview
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isSending}
          disabled={!selected || !candidate?.email}
          onClick={handleSendClicked}
        >
          Send
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
